import {DirectiveOptions} from 'vue';
import {DirectiveBinding} from 'vue/types/options';
import './tip.css';

/**
 * `v-tip="'Text'"`: small dark tooltip for icon-only buttons, styled like the Vuetify tooltip.
 *
 * Why not `title` or `<v-tooltip>`: the native `title` appears late (or never on a v-menu activator) and
 * `<v-tooltip>` needs an activator slot that clashes with menu activators. The directive only listens on
 * the element, so it works on plain buttons, `v-btn` and menu activators alike.
 *
 * - One shared tooltip element in `<body>` (position fixed): no layout shift, never clipped by overflow.
 * - Opens after 250 ms on mouse/pen hover, immediately on keyboard focus (`:focus-visible`); touch is ignored,
 *   so a tap never leaves a tooltip behind. Closes on leave, blur, click, Escape, scroll and resize.
 * - Shown above the element, below it when there is no room; kept inside the viewport horizontally.
 * - Removes a native `title` of the element (no double tooltips). `aria-label` stays the accessible name.
 * - Value: text, falsy for no tooltip. Without an expression (`v-tip`) the `aria-label` is shown.
 * - Disabled buttons fire no mouse events: put `v-tip` (with the disabled reason) on a wrapping
 *   `<span class="app-tip-wrap">`; a disabled direct child then lets the pointer through to the wrapper.
 */

const OPEN_DELAY = 250;
const GAP = 6;
const EDGE = 8;

interface TipState {
    text: string | null;
    useAriaLabel: boolean;
    onPointerEnter: (e: PointerEvent) => void;
    onPointerLeave: () => void;
    onFocus: (e: FocusEvent) => void;
    onHide: () => void;
    onKeydown: (e: KeyboardEvent) => void;
}

type TipElement = HTMLElement & { __tip?: TipState };

let tipEl: HTMLDivElement | null = null;
let current: TipElement | null = null;
let timer: number | undefined;
let listening = false;

function tooltip(): HTMLDivElement {
    if (!tipEl) {
        tipEl = document.createElement('div');
        tipEl.className = 'app-tip';
        tipEl.setAttribute('role', 'tooltip');
        tipEl.id = 'app-tip';
        document.body.appendChild(tipEl);
    }
    return tipEl;
}

function textOf(el: TipElement): string {
    const state = el.__tip;
    if (!state) {
        return '';
    }
    return (state.useAriaLabel ? el.getAttribute('aria-label') : state.text) || '';
}

function onGlobalHide() {
    hide();
}

function setGlobalListeners(on: boolean) {
    if (on === listening) {
        return;
    }
    listening = on;
    const method = on ? 'addEventListener' : 'removeEventListener';
    window[method]('scroll', onGlobalHide, true);
    window[method]('resize', onGlobalHide);
}

function place(el: HTMLElement, tip: HTMLDivElement) {
    const rect = el.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const below = rect.top - height - GAP < EDGE && rect.bottom + height + GAP <= viewportHeight - EDGE;
    const top = below ? rect.bottom + GAP : rect.top - height - GAP;
    const left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, EDGE), Math.max(EDGE, viewportWidth - width - EDGE));
    tip.style.top = `${Math.round(top)}px`;
    tip.style.left = `${Math.round(left)}px`;
    tip.classList.toggle('app-tip--below', below);
}

function show(el: TipElement) {
    window.clearTimeout(timer);
    const text = textOf(el);
    // An open menu of this activator covers the tooltip anyway
    if (!text || !el.isConnected || el.getAttribute('aria-expanded') === 'true') {
        hide();
        return;
    }
    const tip = tooltip();
    tip.textContent = text;
    tip.classList.remove('app-tip--visible');
    tip.style.top = '-9999px';
    tip.style.left = '-9999px';
    current = el;
    // Only describe when the tooltip adds something to the accessible name (e.g. a disabled reason)
    if (el.getAttribute('aria-label') !== text) {
        el.setAttribute('aria-describedby', tip.id);
    }
    place(el, tip);
    tip.classList.add('app-tip--visible');
    setGlobalListeners(true);
}

function hide(el?: TipElement) {
    window.clearTimeout(timer);
    timer = undefined;
    if (el && current && current !== el) {
        return;
    }
    if (current) {
        current.removeAttribute('aria-describedby');
        current = null;
    }
    tipEl?.classList.remove('app-tip--visible');
    setGlobalListeners(false);
}

function isKeyboardFocus(target: EventTarget | null): boolean {
    try {
        return target instanceof Element && target.matches(':focus-visible');
    } catch (e) {
        return false;
    }
}

function applyValue(el: TipElement, binding: DirectiveBinding) {
    const state = el.__tip;
    if (!state) {
        return;
    }
    state.useAriaLabel = !binding.expression;
    state.text = binding.value ? String(binding.value) : null;
    if (el.hasAttribute('title')) {
        el.removeAttribute('title');
    }
    if (current === el) {
        const text = textOf(el);
        if (text) {
            tooltip().textContent = text;
            place(el, tooltip());
        } else {
            hide(el);
        }
    }
}

const TipDirective: DirectiveOptions = {
    bind(el: TipElement, binding: DirectiveBinding) {
        const state: TipState = {
            text: null,
            useAriaLabel: false,
            onPointerEnter: (e: PointerEvent) => {
                if (e.pointerType === 'touch') {
                    return;
                }
                window.clearTimeout(timer);
                timer = window.setTimeout(() => show(el), OPEN_DELAY);
            },
            onPointerLeave: () => hide(el),
            // focusin bubbles: also covers a focused button inside a wrapping element
            onFocus: (e: FocusEvent) => {
                if (isKeyboardFocus(e.target)) {
                    show(el);
                }
            },
            onHide: () => hide(el),
            onKeydown: (e: KeyboardEvent) => {
                if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
                    hide(el);
                }
            },
        };
        el.__tip = state;
        el.classList.add('app-tip-host');
        el.addEventListener('pointerenter', state.onPointerEnter);
        el.addEventListener('pointerleave', state.onPointerLeave);
        el.addEventListener('focusin', state.onFocus);
        el.addEventListener('focusout', state.onHide);
        el.addEventListener('pointerdown', state.onHide);
        el.addEventListener('keydown', state.onKeydown);
        applyValue(el, binding);
    },
    update(el: TipElement, binding: DirectiveBinding) {
        applyValue(el, binding);
    },
    // Vuetify may re-render the title attribute of a component root; strip it again
    componentUpdated(el: TipElement) {
        if (el.hasAttribute('title')) {
            el.removeAttribute('title');
        }
    },
    unbind(el: TipElement) {
        const state = el.__tip;
        if (!state) {
            return;
        }
        hide(el);
        el.removeEventListener('pointerenter', state.onPointerEnter);
        el.removeEventListener('pointerleave', state.onPointerLeave);
        el.removeEventListener('focusin', state.onFocus);
        el.removeEventListener('focusout', state.onHide);
        el.removeEventListener('pointerdown', state.onHide);
        el.removeEventListener('keydown', state.onKeydown);
        el.classList.remove('app-tip-host');
        delete el.__tip;
    },
};

export default TipDirective;
